import { createFileRoute } from "@tanstack/react-router";
import { selectFormMetadataQueryOptions, useFormMetadata } from "@/features/forms/api/selectFormMetadata";
import { BookOpen, Calendar, ExternalLink, Globe, Quote, Users } from "lucide-react";
import { FormBadge, FormBadgeGroup, FormInfoRow, FormSectionCard } from "@patient-watch/ui";

function FormMetadataPage() {
    const { formId } = Route.useParams();
    const { data } = useFormMetadata(formId);
    const { form, research_papers } = data;

    return (
        <div className="mx-auto px-4 py-8 max-w-6xl container">
            {/* Header Section */}
            <div className="mb-8">
                <div className="flex items-start gap-4 mb-6">
                    <div className="bg-indigo-100 p-3 rounded-xl">
                        <BookOpen className="w-8 h-8 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                        <h1 className="mb-2 font-bold text-slate-800 text-4xl">{form.form_name}</h1>
                        {form.form_description && (
                            <p className="text-slate-600 text-xl leading-relaxed">{form.form_description}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Overview Section */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 shadow-sm mb-8 border border-indigo-200 rounded-lg">
                <div className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="bg-indigo-200 p-2 rounded-lg">
                            <BookOpen className="w-6 h-6 text-indigo-700" />
                        </div>
                        <h2 className="font-semibold text-indigo-900 text-2xl">What is the {form.form_name}?</h2>
                    </div>
                    <div className="max-w-none">
                        {form.overview_text && (
                            <p className="mb-4 text-indigo-800 leading-relaxed">{form.overview_text}</p>
                        )}

                        {form.scoring_methodology && (
                            <div className="bg-white/80 shadow-sm backdrop-blur-sm p-4 border border-indigo-200 rounded-lg">
                                <div className="flex items-center gap-2 my-auto">
                                    <BookOpen className="my-auto w-6 h-6 text-indigo-600" />
                                    <h3 className="font-semibold text-indigo-900">Scoring System</h3>
                                </div>
                                <p className="text-indigo-700 text-sm leading-relaxed">{form.scoring_methodology}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Form Details Cards */}
            <div className="gap-6 grid lg:grid-cols-2 mb-8">
                {/* General Information Card */}
                <FormSectionCard
                    icon={BookOpen}
                    iconColor="text-blue-600"
                    iconBgColor="bg-blue-100"
                    title="General Information"
                >
                    {form.scoring && (
                        <FormInfoRow
                            icon={BookOpen}
                            label="Scoring"
                            value={<FormBadge variant="scoring">{form.scoring.replace("_", " ")}</FormBadge>}
                        />
                    )}

                    {form.time_to_complete && (
                        <FormInfoRow
                            icon={Calendar}
                            label="Time to Complete"
                            value={`${form.time_to_complete} minutes`}
                        />
                    )}

                    <FormInfoRow icon={BookOpen} label="Pages" value={form.number_of_pages} />

                    <FormInfoRow icon={Users} label="Questions" value={form.number_of_questions} />

                    <FormInfoRow
                        icon={BookOpen}
                        iconClassName={form.is_licensed ? "text-green-500" : "text-slate-500"}
                        label="Licensed"
                        value={form.is_licensed ? "Yes" : "No"}
                    />

                    {Number(form.price) > 0 && <FormInfoRow icon={BookOpen} label="Price" value={`£${form.price}`} />}
                </FormSectionCard>

                {/* Clinical Information Card */}
                <FormSectionCard
                    icon={Users}
                    iconColor="text-green-600"
                    iconBgColor="bg-green-100"
                    title="Clinical Information"
                >
                    <FormBadgeGroup
                        icon={BookOpen}
                        label="Medical Specialties"
                        items={form.medical_specialties || []}
                        variant="specialty"
                    />

                    <FormBadgeGroup
                        icon={BookOpen}
                        label="Anatomic Areas"
                        items={form.orthopaedic_areas || []}
                        variant="anatomic"
                    />

                    <FormBadgeGroup
                        icon={BookOpen}
                        label="Indications"
                        items={form.indications || []}
                        variant="indication"
                    />
                </FormSectionCard>
            </div>

            {/* Instructions Section */}
            {form.form_instructions && (
                <div className="bg-white shadow-sm mb-8 border border-gray-200 rounded-lg">
                    <div className="p-6 border-gray-200 border-b">
                        <h3 className="font-semibold text-lg">Instructions</h3>
                    </div>
                    <div className="p-6">
                        <p className="text-slate-700 leading-relaxed">{form.form_instructions}</p>
                    </div>
                </div>
            )}

            {/* Research Literature Section */}
            {research_papers.length > 0 && (
                <div className="bg-white shadow-sm mb-8 border border-gray-200 rounded-lg">
                    <div className="p-6 border-gray-200 border-b">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="bg-green-100 p-2 rounded-lg">
                                <Quote className="w-5 h-5 text-green-600" />
                            </div>
                            <h3 className="font-semibold text-lg">Research Literature</h3>
                        </div>
                        <p className="mt-1 text-slate-600 text-sm">
                            {research_papers.length} paper{research_papers.length !== 1 ? "s" : ""} referencing this
                            form
                        </p>
                    </div>
                    <div className="p-6">
                        <div className="space-y-4">
                            {research_papers.map((paper) => (
                                <div
                                    key={paper.paper_id}
                                    className="bg-slate-50 p-4 border border-slate-200 rounded-lg"
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="flex-1 mr-4 font-medium text-slate-800 text-sm">
                                            {paper.title}
                                        </h4>
                                        <div className="flex gap-2">
                                            {paper.citation_count && (
                                                <span className="flex items-center gap-1 bg-blue-100 px-2 py-1 rounded text-blue-800 text-xs">
                                                    <BookOpen className="w-3 h-3" />
                                                    {paper.citation_count} citations
                                                </span>
                                            )}
                                            {paper.impact_factor && (
                                                <span className="flex items-center gap-1 bg-green-100 px-2 py-1 rounded text-green-800 text-xs">
                                                    <Quote className="w-3 h-3" />
                                                    IF: {paper.impact_factor}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 mb-2 text-slate-600 text-xs">
                                        <Users className="w-3 h-3" />
                                        {paper.authors}
                                    </div>

                                    <div className="flex items-center gap-2 mb-2 text-slate-500 text-xs">
                                        <Calendar className="w-3 h-3" />
                                        <em>{paper.journal}</em> ({paper.year})
                                        {paper.doi && (
                                            <a
                                                href={`https://doi.org/${paper.doi}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1 ml-2 text-blue-600 hover:text-blue-800"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                                DOI: {paper.doi}
                                            </a>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {paper.study_type && (
                                            <FormBadge variant="study-type" className="flex items-center gap-1">
                                                <BookOpen className="w-3 h-3" />
                                                {paper.study_type.replace("_", " ")}
                                            </FormBadge>
                                        )}
                                        {paper.sample_size && (
                                            <FormBadge variant="prevalence" className="flex items-center gap-1">
                                                <Users className="w-3 h-3" />
                                                n={paper.sample_size}
                                            </FormBadge>
                                        )}
                                        {paper.country && (
                                            <FormBadge variant="country" className="flex items-center gap-1">
                                                <Globe className="w-3 h-3" />
                                                {paper.country}
                                            </FormBadge>
                                        )}
                                    </div>

                                    {paper.population && (
                                        <div className="flex items-center gap-2 mb-2 text-slate-500 text-xs">
                                            <Users className="w-3 h-3" />
                                            <strong>Population:</strong> {paper.population}
                                        </div>
                                    )}

                                    {paper.abstract && (
                                        <div className="flex items-start gap-2 text-slate-600 text-xs">
                                            <Quote className="flex-shrink-0 mt-0.5 w-3 h-3" />
                                            <div>
                                                <strong>Abstract:</strong> {paper.abstract.substring(0, 200)}...
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Developer Information Section */}
            {(form.developer_info || form.copyright_info) && (
                <div className="bg-white shadow-sm mb-8 border border-gray-200 rounded-lg">
                    <div className="p-6 border-gray-200 border-b">
                        <h3 className="font-semibold text-lg">About the Developers</h3>
                    </div>
                    <div className="p-6">
                        {form.developer_info && <p className="text-slate-700 leading-relaxed">{form.developer_info}</p>}

                        {form.copyright_info && (
                            <div className="bg-yellow-50 mt-4 p-3 border border-yellow-200 rounded-lg">
                                <h4 className="mb-1 font-medium text-yellow-900">Copyright Information</h4>
                                <p className="text-yellow-800 text-sm">{form.copyright_info}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Limitations Section */}
            {form.limitations && (
                <div className="bg-white shadow-sm mb-8 border border-gray-200 rounded-lg">
                    <div className="p-6 border-gray-200 border-b">
                        <div className="flex items-center gap-3">
                            <div className="bg-yellow-100 p-2 rounded-lg">
                                <Quote className="w-5 h-5 text-yellow-600" />
                            </div>
                            <h3 className="font-semibold text-lg">Limitations & Considerations</h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <p className="text-slate-700 leading-relaxed">{form.limitations}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

export const Route = createFileRoute("/_app/builder/forms/$formId/metadata")({
    component: FormMetadataPage,
    loader: async ({ context, params }) => {
        const formMetadata = await context?.queryClient.ensureQueryData(selectFormMetadataQueryOptions(params.formId));
        return { formMetadata };
    },
});
